import { useEffect, useState } from "react";
import { fetchAPI } from "@/lib/api";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Heart, Shield, Users, Leaf, Compass, BookOpen } from "lucide-react";
import { InquiryForm } from "@/components/InquiryForm";
import guideImage from "@/assets/spiritual-guide.jpg";
import guideImageWebp from "@/assets/spiritual-guide.webp";
import templeImage from "@/assets/baelleaves-main.jpg";
import templeImageWebp from "@/assets/baelleaves-main.webp";

interface Circuit {
  id: number;
  title: string;
  description: string;
}

const values = [
  {
    icon: Compass,
    title: "Reactivating Spiritual Circuits",
    description: "We believe in awakening the dormant spiritual pathways that connect seekers to India's sacred heritage.",
  },
  {
    icon: Shield,
    title: "Authentic Experiences",
    description: "Every journey is designed to provide genuine encounters with India's living spiritual traditions, not tourist versions.",
  },
  {
    icon: Users,
    title: "Cultural Preservation",
    description: "We work directly with local communities, temples, and ashrams to preserve and share timeless traditions.",
  },
  {
    icon: BookOpen,
    title: "India's Heritage Philosophy",
    description: "Exploring the deep relationship between temples, time, and the cultural complexity of India's sacred geography.",
  },
];


const About = () => {
  const [circuits, setCircuits] = useState<Circuit[]>([]);

  useEffect(() => {
    fetchAPI("?type=circuits")
      .then((data: any) => setCircuits(data))
      .catch((err) => console.error("Error fetching circuits:", err));
  }, []);

  return (
    <div className="min-h-screen bg-background">


      {/* Hero Section */}
      {/* <section className="pt-32 pb-16 bg-gradient-to-b from-sand to-background">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-primary text-sm tracking-[0.2em] uppercase font-medium">
              Our Mission
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mt-4 mb-6">
              Reactivating the <span className="text-primary">Spiritual Circuits</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Bael Leaves is dedicated to preserving and sharing India's timeless heritage 
              through authentic journeys that highlight diverse traditions, historical landmarks, 
              and sacred sites across the subcontinent.
            </p>
          </div>
        </div>
      </section> */}

      {/* Mission Story */}
      <section className="py-16 pt-32">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                Bridging Seekers to <span className="text-primary">Sacred India</span>
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  India's spiritual landscape is vast and profound—home to countless temples, 
                  ashrams, and sacred sites that have drawn seekers for millennia. Yet in the 
                  modern age, many of these spiritual circuits remain dormant, their wisdom 
                  waiting to be rediscovered.
                </p>
                <p>
                  At Bael Leaves, we believe that travel can be a form of spiritual practice. 
                  Our mission is to reactivate these sacred pathways, creating meaningful 
                  connections between international travelers and India's living heritage.
                </p>
                <p>
                  We curate journeys that go beyond sightseeing. Each circuit is thoughtfully 
                  designed to immerse you in authentic experiences—whether you seek the 
                  transformative power of ancient temples, the healing wisdom of Ayurveda, 
                  or the profound silence of Himalayan retreats.
                </p>
                <p>
                  Our approach is inspired by "India's Heritage"—the exploration of how temples, 
                  time, and cultural complexity interweave to create India's unique spiritual 
                  geography. Every journey we design reflects this philosophy.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl blur-2xl" />
              <picture>
                <source srcSet={templeImageWebp} type="image/webp" />
                <img 
                  src="https://images.unsplash.com/photo-1599827552599-0f9b57569106?q=80&w=2670&auto=format&fit=crop" 
                  alt="Bilva Leaf" 
                  className="relative w-full h-[500px] object-cover rounded-2xl shadow-elevated"
                  loading="lazy"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* Spiritual Circuits */}
      <section className="py-16 bg-sand">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
              Our <span className="text-primary">Spiritual Circuits</span>
            </h2>
            <p className="text-muted-foreground">
              Six unique pathways to explore India's sacred heritage, each offering 
              a distinct journey of discovery and transformation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {circuits.map((circuit) => (
              <div
                key={circuit.id}
                className="bg-background rounded-2xl p-8 shadow-soft hover:shadow-elevated transition-shadow border border-border/50"
              >
                <h3 className="font-serif text-xl text-foreground mb-2">
                  {circuit.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {circuit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
              Our <span className="text-primary">Guiding Principles</span>
            </h2>
            <p className="text-muted-foreground">
              Everything we do is rooted in respect, authenticity, and preservation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div
                key={value.title}
                className="bg-background rounded-2xl p-8 shadow-soft hover:shadow-elevated transition-shadow border border-border/50"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-serif text-xl text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guides */}
      <section className="py-16 bg-sand">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <picture>
                <source srcSet={guideImageWebp} type="image/webp" />
                <img
                  src={guideImage}
                  alt="Spiritual guide in India"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[400px] object-cover rounded-2xl shadow-elevated"
                />
              </picture>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                Expert Guides & <span className="text-primary">Local Wisdom</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Our journeys are led by knowledgeable guides who don't just know the 
                paths—they live them. From temple priests to yoga masters, Ayurvedic 
                practitioners to meditation teachers, our network of local experts 
                ensures authentic and meaningful experiences.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We partner with respected ashrams, heritage sites, and spiritual 
                communities across India, ensuring you have access to genuine teachings 
                and traditions—not tourist versions of sacred practices.
              </p>
              <InquiryForm 
                trigger={<Button variant="sacred">Connect With Us</Button>}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl mb-4">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-primary-foreground/70 max-w-2xl mx-auto mb-8">
            Connect with our team to discuss your spiritual aspirations and find 
            the perfect circuit for your transformative journey through India.
          </p>
          <InquiryForm 
            trigger={
              <Button 
                variant="outline" 
                size="lg"
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 border-none"
              >
                Begin Your Journey
              </Button>
            }
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
